/**
 * Allow-listed iframe hosts for the waiting-room livestream embed.
 *
 * We render `events.embed_code` via innerHTML on the speaker's waiting
 * room, which means any admin with write access to events could otherwise
 * inject `<script>` tags that execute in every speaker's browser — and
 * with localStorage being the session store, that's a full account
 * takeover vector.
 *
 * The sanitiser parses the raw embed HTML, keeps only iframe elements
 * pointing at hosts on this list, and strips every attribute except the
 * safe-by-spec ones. Width/height get forced via wrapper CSS so even
 * a malicious `width=1` can't hide the iframe.
 */
const ALLOWED_HOSTS = [
  // YouTube
  'youtube.com',
  'youtu.be',
  'youtube-nocookie.com',
  // Vimeo
  'vimeo.com',
  'player.vimeo.com',
  // Facebook Live
  'facebook.com',
  'www.facebook.com',
  // Twitch
  'twitch.tv',
  'player.twitch.tv',
  // LinkedIn Live
  'linkedin.com',
  // Dailymotion (occasionally used in EU markets)
  'dailymotion.com',
  'geo.dailymotion.com',
];

const KEEP_ATTRS = new Set([
  'src',
  'allow',
  'allowfullscreen',
  'allowtransparency',
  'frameborder',
  'scrolling',
  'title',
]);

function isAllowedSrc(src: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(src);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
  const host = parsed.hostname.toLowerCase();
  return ALLOWED_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
}

/**
 * Returns HTML containing only the allow-listed iframes from `raw`,
 * with everything else (script tags, inline event handlers, third-party
 * stylesheets, arbitrary nodes) stripped.
 *
 * Returns an empty string if the embed contains nothing safe to render.
 */
export function sanitiseEmbed(raw: string): string {
  if (typeof window === 'undefined' || !raw) return '';
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(raw, 'text/html');
  } catch {
    return '';
  }
  const iframes = Array.from(doc.querySelectorAll('iframe'));
  const safe: string[] = [];
  for (const iframe of iframes) {
    const src = iframe.getAttribute('src') ?? '';
    if (!isAllowedSrc(src)) continue;
    const clean = document.createElement('iframe');
    for (const attr of Array.from(iframe.attributes)) {
      if (!KEEP_ATTRS.has(attr.name.toLowerCase())) continue;
      // Inline event handlers like onload="…" got dropped by KEEP_ATTRS
      // already. Belt-and-braces: refuse anything starting with `on`.
      if (attr.name.toLowerCase().startsWith('on')) continue;
      clean.setAttribute(attr.name, attr.value);
    }
    safe.push(clean.outerHTML);
  }
  return safe.join('');
}
